/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect, useState } from 'react';
import { getEmojiProviderWithCustomEmoji } from '@jira/platform__atlaskit-emoji-provider/src/emoji.tsx';
import type { EmojiDescription, EmojiProvider } from '@atlaskit/emoji/types';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createStore,
	createContainer,
	createSelector,
	type StoreActionApi,
	type BoundActions,
	type HookFunction,
	type OverrideContainerComponent,
} from '@atlassian/react-sweet-state';
import type { State, Props } from './State.types.tsx';

const actions = {
	loadEmoji:
		(emojiId: string) =>
		async ({ getState, setState }: StoreActionApi<State>): Promise<void> => {
			const { provider } = getState();
			if (!provider) {
				return;
			}

			const resolvedProvider = await provider;
			const optionalDescription = await resolvedProvider.findById(emojiId);
			const emoji =
				optionalDescription !== undefined
					? await resolvedProvider.getMediaEmojiDescriptionURLWithInlineToken(optionalDescription)
					: optionalDescription;

			setState({ cachedEmojis: { ...getState().cachedEmojis, [emojiId]: emoji ?? null } });
		},
};

type Actions = typeof actions;

const initialState: State = {
	provider: undefined,
	cachedEmojis: {},
};

const EmojiStore = createStore<State, Actions>({
	initialState,
	actions,
	name: 'PolarisEmojiStore',
});

const useEmojis = createHook(EmojiStore);

/**
 * used in various places to render emojis
 */
export const useEmoji = (emojiId?: string): EmojiDescription | undefined => {
	const [emoji, setEmoji] = useState<EmojiDescription | undefined>(undefined);
	const [{ provider }] = useEmojis();

	useEffect(() => {
		if (emojiId !== undefined && provider !== undefined) {
			provider.then((resolvedProvider: EmojiProvider) => {
				Promise.resolve(resolvedProvider.findById(emojiId))
					.then((optionalDescription) =>
						optionalDescription !== undefined
							? resolvedProvider.getMediaEmojiDescriptionURLWithInlineToken(optionalDescription)
							: optionalDescription,
					)
					.then(setEmoji);
			});
		} else {
			// oxlint-disable-next-line react/react-compiler
			setEmoji(undefined);
		}
	}, [provider, emojiId]);

	return emojiId !== undefined ? emoji : undefined;
};

export const useEmojiById: HookFunction<
	EmojiDescription | null | undefined,
	BoundActions<State, Actions>,
	{
		emojiId?: string;
	}
> = createHook(EmojiStore, {
	selector: createSelector(
		(state, { emojiId }: { emojiId?: string }) =>
			emojiId ? state.cachedEmojis[emojiId] : undefined,
		(emoji) => emoji,
	),
});

/**
 * used to provide ak/emoji-picker with an emoji provider
 */
export const useEmojiProviderPromise = (): Promise<EmojiProvider> => {
	const [{ provider }] = useEmojis();
	return provider !== undefined
		? provider
		: Promise.reject(new Error('emoji provider not initialized'));
};

export const EmojiContainer: OverrideContainerComponent<Props> = createContainer<
	State,
	Actions,
	Props
>(EmojiStore, {
	onInit:
		() =>
		(storeActionApi, { cloudId, currentUser }) => {
			storeActionApi.setState({
				provider: getEmojiProviderWithCustomEmoji(cloudId, currentUser) || undefined,
			});
		},
});
